// Nonogram clues and solving. Cells: 1 filled, 0 empty, -1 unknown.
// The app counts a board solved when every line matches its clues, so a puzzle is only fair when
// those clues lead to exactly one picture, ideally by line logic alone (no guessing).

export const lineRuns = (line) => {
  const runs = [];
  let run = 0;
  line.forEach(v => {
    if (v === 1) run++;
    else if (run) { runs.push(run); run = 0; }
  });
  if (run) runs.push(run);
  return runs;
};

export const rowOf = (cells, w, r) => cells.slice(r * w, r * w + w);
export const colOf = (cells, w, h, c) => Array.from({ length: h }, (_, r) => cells[r * w + c]);

export const cluesOf = (cells, w, h) => ({
  rows: Array.from({ length: h }, (_, r) => lineRuns(rowOf(cells, w, r))),
  cols: Array.from({ length: w }, (_, c) => lineRuns(colOf(cells, w, h, c))),
});

// Every value each cell can take over all placements of the clues that agree with the known cells.
// Returns the line with forced cells filled in (others -1), or null when the clues can't fit.
export const solveLine = (clues, line) => {
  const n = line.length, k = clues.length;
  // fit[j][p]: blocks j.. fit into cells p..n-1, where p is the first cell not yet decided
  const fit = Array.from({ length: k + 1 }, () => new Array(n + 2).fill(false));
  for (let j = k; j >= 0; j--) {
    for (let p = n; p >= 0; p--) {
      if (p === n) { fit[j][p] = j === k; continue; }
      let ok = line[p] !== 1 && fit[j][p + 1];
      if (!ok && j < k) ok = blockFits(line, p, clues[j]) && afterBlock(line, p + clues[j], n, fit[j + 1]);
      fit[j][p] = ok;
    }
  }
  if (!fit[0][0]) return null;

  const canFill = new Array(n).fill(false);
  const canEmpty = new Array(n).fill(false);
  const reached = Array.from({ length: k + 1 }, () => new Array(n + 1).fill(false));
  reached[0][0] = true;
  for (let p = 0; p < n; p++) {
    for (let j = 0; j <= k; j++) {
      if (!reached[j][p] || !fit[j][p]) continue;
      if (line[p] !== 1 && fit[j][p + 1]) {
        canEmpty[p] = true;
        reached[j][p + 1] = true;
      }
      if (j < k && blockFits(line, p, clues[j])) {
        const end = p + clues[j];
        if (afterBlock(line, end, n, fit[j + 1])) {
          for (let i = p; i < end; i++) canFill[i] = true;
          if (end < n) { canEmpty[end] = true; reached[j + 1][end + 1] = true; } else reached[j + 1][n] = true;
        }
      }
    }
  }
  return line.map((v, i) => (canFill[i] && canEmpty[i] ? -1 : canFill[i] ? 1 : 0));
};

const blockFits = (line, p, len) => {
  if (p + len > line.length) return false;
  for (let i = p; i < p + len; i++) if (line[i] === 0) return false;
  return true;
};

const afterBlock = (line, end, n, fitNext) => (end === n ? fitNext[n] : line[end] !== 1 && fitNext[end + 1]);

// Line logic until nothing changes. Returns { cells, passes } or null on a contradiction.
export const propagate = (w, h, clues, start) => {
  const cells = start ? [...start] : new Array(w * h).fill(-1);
  let dirtyRows = new Set(Array.from({ length: h }, (_, i) => i));
  let dirtyCols = new Set(Array.from({ length: w }, (_, i) => i));
  let passes = 0;
  while (dirtyRows.size || dirtyCols.size) {
    passes++;
    const nextCols = new Set();
    for (const r of dirtyRows) {
      const solved = solveLine(clues.rows[r], rowOf(cells, w, r));
      if (!solved) return null;
      solved.forEach((v, c) => {
        if (v !== -1 && cells[r * w + c] === -1) { cells[r * w + c] = v; nextCols.add(c); }
      });
    }
    const nextRows = new Set();
    for (const c of new Set([...dirtyCols, ...nextCols])) {
      const solved = solveLine(clues.cols[c], colOf(cells, w, h, c));
      if (!solved) return null;
      solved.forEach((v, r) => {
        if (v !== -1 && cells[r * w + c] === -1) { cells[r * w + c] = v; nextRows.add(r); }
      });
    }
    dirtyRows = nextRows;
    dirtyCols = new Set();
  }
  return { cells, passes };
};

// Counts solutions up to `limit` with propagation plus guessing. `exhausted` means the search
// stopped at the node budget, so the count is a lower bound.
export const countSolutions = (w, h, clues, { limit = 2, maxNodes = 4000 } = {}) => {
  const solutions = [];
  let nodes = 0;
  let exhausted = false;
  const search = (cells) => {
    if (solutions.length >= limit) return;
    if (++nodes > maxNodes) { exhausted = true; return; }
    const result = propagate(w, h, clues, cells);
    if (!result) return;
    const unknown = result.cells.indexOf(-1);
    if (unknown === -1) { solutions.push(result.cells); return; }
    for (const v of [1, 0]) {
      const next = [...result.cells];
      next[unknown] = v;
      search(next);
      if (solutions.length >= limit || exhausted) return;
    }
  };
  search(null);
  return { solutions, exhausted };
};

// Everything the editor shows about a drawing.
export const analyze = (w, h, cells) => {
  const clues = cluesOf(cells, w, h);
  const filled = cells.reduce((a, v) => a + (v === 1), 0);
  const logic = propagate(w, h, clues);
  const unresolved = logic ? logic.cells.reduce((a, v) => a + (v === -1), 0) : w * h;
  let status;
  let alternative = null;
  if (filled === 0) status = 'empty';
  else if (unresolved === 0) status = 'logic';
  else {
    const { solutions, exhausted } = countSolutions(w, h, clues);
    if (solutions.length > 1) {
      status = 'multiple';
      alternative = solutions.find(s => s.some((v, i) => v !== cells[i])) || null;
    } else status = exhausted ? 'unknown' : 'guess';
  }
  const maxClues = Math.max(...clues.rows.map(c => c.length), ...clues.cols.map(c => c.length));
  return {
    clues,
    filled,
    status,
    unresolvedCells: logic ? logic.cells.map(v => v === -1) : cells.map(() => true),
    unresolved,
    passes: logic?.passes || 0,
    alternative,
    maxClues,
  };
};

export const STATUS_TEXT = {
  empty: 'Tyhjä ruudukko',
  logic: 'Ratkeaa pelkällä päättelyllä',
  guess: 'Yksikäsitteinen, mutta vaatii arvaamista',
  multiple: 'Useita ratkaisuja',
  unknown: 'Ei ratkea päättelyllä, ratkaisujen määrä ei selvinnyt',
};

// Rough difficulty from how many rounds of line logic the solve takes.
export const difficultyText = (passes, w, h) => {
  const per = passes / Math.max(w, h);
  if (per < 0.35) return 'helppo';
  if (per < 0.7) return 'keskitaso';
  return 'vaikea';
};

const rand = (n) => Math.floor(Math.random() * n);

// An abstract warm-up: a random mirrored pattern that line logic solves on its own.
export const randomWarmup = (w = 5, h = 5, { tries = 400, minFill = 0.4, maxFill = 0.72 } = {}) => {
  for (let t = 0; t < tries; t++) {
    const cells = new Array(w * h).fill(0);
    const mirrorX = Math.random() < 0.7;
    const mirrorY = !mirrorX || Math.random() < 0.25;
    const density = minFill + Math.random() * (maxFill - minFill);
    for (let r = 0; r < (mirrorY ? Math.ceil(h / 2) : h); r++) {
      for (let c = 0; c < (mirrorX ? Math.ceil(w / 2) : w); c++) {
        if (Math.random() >= density) continue;
        [[r, c], [r, w - 1 - c], [h - 1 - r, c], [h - 1 - r, w - 1 - c]].forEach(([rr, cc], i) => {
          if ((i === 1 || i === 3) && !mirrorX) return;
          if ((i === 2 || i === 3) && !mirrorY) return;
          cells[rr * w + cc] = 1;
        });
      }
    }
    const filled = cells.reduce((a, v) => a + v, 0) / cells.length;
    if (filled < minFill || filled > maxFill + 0.08) continue;
    const clues = cluesOf(cells, w, h);
    if (clues.rows.some(c => !c.length) || clues.cols.some(c => !c.length)) continue;
    if (propagate(w, h, clues)?.cells.every(v => v !== -1)) return cells;
  }
  const cells = new Array(w * h).fill(0);
  for (let i = 0; i < cells.length; i++) cells[i] = rand(2);
  return cells;
};

export const encodeCells = (cells) => cells.map(v => (v === 1 ? '1' : '0')).join('');
export const decodeCells = (s, n) => Array.from({ length: n }, (_, i) => (s?.[i] === '1' ? 1 : 0));
